/**
 * Nexus Admin API Service
 * API client for admin dashboard endpoints
 */

import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// Separate axios instance for nexus admin
export const nexusApi = axios.create({
  baseURL: `${API_BASE_URL}/nexus`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add nexus admin token
nexusApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nexus_access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for auth errors
nexusApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nexus_access_token')
      window.location.href = '/nexus/login'
    }
    return Promise.reject(error)
  }
)

// Types
export interface Admin {
  id: string
  email: string
  name: string
  is_active: boolean
  can_manage_users: boolean
  can_manage_abilities: boolean
  can_manage_billing: boolean
  can_manage_apis: boolean
}

export interface User {
  id: string
  email: string
  username: string
  full_name: string
  phone_number: string | null
  role: string
  is_active: boolean
  is_verified: boolean
  wallet_balance: number | null
  created_at: string
  last_login_at: string | null
}

export interface Ability {
  id: string
  tool_name: string
  display_name: string
  description: string
  category: string
  is_enabled: boolean
  status: string
  status_message: string | null
  is_billable: boolean
  billing_enabled: boolean
  billing_rate: number
}

export interface APIService {
  id: string
  name: string
  display_name: string
  service_type: string
  is_active: boolean
  health_status: string
  last_health_check: string | null
  current_balance: number | null
  balance_unit: string | null
  low_balance_threshold: number | null
  critical_balance_threshold: number | null
  alert_email: string
}

export interface DashboardOverview {
  users: {
    total_users: number
    active_24h: number
    active_7d: number
    active_30d: number
    new_today: number
    new_this_week: number
  }
  revenue: {
    total_today: number
    total_week: number
    total_month: number
    total_all_time: number
  }
  wallets: {
    total_balance: number
    total_deposits_today: number
    total_debits_today: number
    avg_balance: number
  }
  api_health: {
    total_services: number
    healthy: number
    degraded: number
    down: number
    low_balance_alerts: number
  }
  abilities: {
    total_abilities: number
    enabled: number
    disabled: number
    with_issues: number
    in_maintenance: number
  }
  generated_at: string
}

// Auth API
export const nexusAuthApi = {
  login: async (email: string, password: string) => {
    const response = await nexusApi.post('/auth/login', { email, password })
    return response.data
  },

  logout: async () => {
    const response = await nexusApi.post('/auth/logout')
    localStorage.removeItem('nexus_access_token')
    return response.data
  },

  me: async () => {
    const response = await nexusApi.get('/auth/me')
    return response.data
  },
}

// Dashboard API
export const nexusDashboardApi = {
  getOverview: async (): Promise<DashboardOverview> => {
    const response = await nexusApi.get('/dashboard/overview')
    return response.data
  },
}

// Users API
export const nexusUsersApi = {
  list: async (page = 1, pageSize = 20) => {
    const response = await nexusApi.get('/users', {
      params: { page, page_size: pageSize },
    })
    return response.data
  },

  get: async (id: string) => {
    const response = await nexusApi.get(`/users/${id}`)
    return response.data
  },

  update: async (id: string, data: Partial<User>) => {
    const response = await nexusApi.patch(`/users/${id}`, data)
    return response.data
  },

  getTransactions: async (id: string, page = 1, pageSize = 20) => {
    const response = await nexusApi.get(`/users/${id}/transactions`, {
      params: { page, page_size: pageSize },
    })
    return response.data
  },

  getUserCharges: async (id: string, startDate?: string, endDate?: string, includeTransactions = true) => {
    const response = await nexusApi.get(`/users/${id}/charges`, {
      params: { start_date: startDate, end_date: endDate, include_transactions: includeTransactions },
    })
    return response.data
  },
}

// Abilities API
export const nexusAbilitiesApi = {
  list: async (): Promise<{ abilities: Ability[] }> => {
    const response = await nexusApi.get('/abilities')
    return response.data
  },

  get: async (toolName: string): Promise<Ability> => {
    const response = await nexusApi.get(`/abilities/${toolName}`)
    return response.data
  },

  update: async (toolName: string, data: Partial<Ability>) => {
    const response = await nexusApi.patch(`/abilities/${toolName}`, data)
    return response.data
  },

  setUserOverride: async (toolName: string, userId: string, data: { is_enabled?: boolean; billing_rate_override?: number }) => {
    const response = await nexusApi.post(`/abilities/${toolName}/user-override`, {
      user_id: userId,
      ...data,
    })
    return response.data
  },

  deleteUserOverride: async (toolName: string, userId: string) => {
    const response = await nexusApi.delete(`/abilities/${toolName}/user-override/${userId}`)
    return response.data
  },
}

// API Services API
export const nexusAPIServicesApi = {
  list: async (): Promise<{ services: APIService[] }> => {
    const response = await nexusApi.get('/api-services')
    return response.data
  },

  getHealth: async () => {
    const response = await nexusApi.get('/api-services/health')
    return response.data
  },

  get: async (name: string) => {
    const response = await nexusApi.get(`/api-services/${name}`)
    return response.data
  },

  update: async (name: string, data: Partial<APIService>) => {
    const response = await nexusApi.patch(`/api-services/${name}`, data)
    return response.data
  },

  checkBalance: async (name: string) => {
    const response = await nexusApi.post(`/api-services/${name}/check-balance`)
    return response.data
  },

  checkAll: async () => {
    const response = await nexusApi.post('/api-services/check-all')
    return response.data
  },
}

// Revenue API
export const nexusRevenueApi = {
  getSummary: async (period = 'daily', startDate?: string, endDate?: string) => {
    const response = await nexusApi.get('/revenue/summary', {
      params: { period, start_date: startDate, end_date: endDate },
    })
    return response.data
  },

  getByAbility: async (startDate?: string, endDate?: string) => {
    const response = await nexusApi.get('/revenue/by-ability', {
      params: { start_date: startDate, end_date: endDate },
    })
    return response.data
  },

  getByUser: async (limit = 10) => {
    const response = await nexusApi.get('/revenue/by-user', {
      params: { limit },
    })
    return response.data
  },
}

export default nexusApi
